// context/NotificationContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { GlobalContext } from "./GlobalContext";
import { resolvePermissions } from "./permissions";
import { apiFetch } from './api.js';

const NotificationContext = createContext();

//The Notification API is not universal: iOS Safari only exposes it inside an
//installed PWA (and not at all before 16.4). NotificationProvider wraps the whole
//authenticated tree, so an unguarded reference here is a blank app, not a missing
//toast. Feature-detect on `window` — a bare `Notification` throws ReferenceError.
const canUseBrowserNotifications = () =>
  typeof window !== 'undefined' && 'Notification' in window;

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const globals = useContext(GlobalContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const user = globals || {};
  
  const socket = globals?.socket || null;

  // Fetch initial notifications and unread count
  const fetchNotifications = useCallback(async () => {
    if (!user?.DealershipID) return;
    console.log('Fetching notifications for DealershipID:', user.DealershipID);
    try {
      const response = await apiFetch('/notifications/recent', { body: {
          DealershipID: user.DealershipID,
          limit: 50
        } });

      const data = await response.json();
      if (data.success) {
        setNotifications(data.notifications);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.DealershipID]);

  const fetchUnreadCount = useCallback(async () => {
    if (!user?.DealershipID) return;
    console.log('Fetching unread count for DealershipID:', user.DealershipID);
    try {
      const response = await apiFetch('/notifications/unread-count', { body: {
          DealershipID: user.DealershipID
        } });

      const data = await response.json();
      if (data.success) {
        setUnreadCount(data.unreadCount);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, [user?.DealershipID]);

  // Listen for new notifications via socket
// In NotificationContext.jsx
// Update the handleNewNotification function

  useEffect(() => {
    if (!socket || !user) return;

    const handleNewNotification = (notification) => {
      // Don't show notifications for own actions
      if (notification.CreatedBy === user.UserID) {
        return;
      }

      // RequiredRoles holds catalog permission strings (or '*' for everyone);
      // deliver if the user holds any of them.
      const held = resolvePermissions(user?.userData);
      const shouldReceive = notification.RequiredRoles.includes('*') ||
        notification.RequiredRoles.some(perm => held.has(perm));

      if (shouldReceive) {
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
        
        // Optional: Show browser notification.
        //`window.Notification` is absent on iOS Safari outside an installed PWA,
        //so the bare identifier would throw a ReferenceError rather than be undefined.
        if (canUseBrowserNotifications() && Notification.permission === 'granted') {
          new Notification(notification.Message, {
            icon: '/logo.png',
            tag: `notification-${notification.NotificationID}`
          });
        }
      }
    };

    socket.on('new_notification', handleNewNotification);

    return () => {
      socket.off('new_notification', handleNewNotification);
    };
  }, [socket, user]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount]);

  // Request browser notification permission
  useEffect(() => {
    if (canUseBrowserNotifications() && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const markAsRead = async (notificationID) => {
    try {
      const response = await apiFetch('/notifications/mark-read', { body: { NotificationID: notificationID } });

      if (response.ok) {
        setNotifications(prev =>
          prev.map(n =>
            n.NotificationID === notificationID ? { ...n, IsRead: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await apiFetch('/notifications/mark-all-read', { body: { DealershipID: user.DealershipID } });

      const data = await response.json();
      if (data.success) {
        setNotifications(prev =>
          prev.map(n => ({ ...n, IsRead: true }))
        );
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refreshNotifications: fetchNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};